import { getInitData, isInTelegram, openLink } from "./telegram.js";

const STORAGE_KEY = "rcf_session";

let session = readStoredSession();
let initPromise = null;
let listeners = new Set();

function readStoredSession() {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(s) {
  session = s;
  try {
    if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {}
  for (const cb of listeners) cb(s);
}

export function getSession() {
  return session;
}

export function onSessionChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function signOut() {
  persist(null);
}

// Browser callers must invoke this from /login on a successful widget callback.
export async function authWithWidget(payload) {
  const res = await fetch("/api/v1/auth/telegram-widget", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const detail = await safeJson(res);
    throw new Error(`auth_widget_failed:${res.status}:${detail?.reason || ""}`);
  }
  const next = await res.json();
  persist(next);
  return next;
}

async function authWithInitData() {
  const initData = getInitData();
  if (!initData) throw new Error("not_in_telegram");
  const res = await fetch("/api/v1/auth/init", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData }),
  });
  if (!res.ok) {
    const detail = await safeJson(res);
    throw new Error(`auth_init_failed:${res.status}:${detail?.reason || ""}`);
  }
  const next = await res.json();
  persist(next);
  return next;
}

// Returns the current session, refreshing via Telegram initData when running
// inside a Mini App and no session exists yet. In a regular browser without
// a stored session this throws `not_authenticated` — callers should redirect
// the user to /login so they can sign in via the Login Widget.
export async function ensureSession() {
  if (session) return session;
  if (!isInTelegram()) throw new Error("not_authenticated");
  if (!initPromise) initPromise = authWithInitData().finally(() => { initPromise = null; });
  return initPromise;
}

export async function api(path, opts = {}) {
  const s = await ensureSession();
  const res = await fetch(path, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${s.token}`,
      ...(opts.headers || {}),
    },
  });
  if (res.status === 401) {
    // Stored token is no longer valid. Inside Telegram we can transparently
    // re-bootstrap; in a regular browser we need the user back at /login.
    persist(null);
    if (!isInTelegram()) throw new Error("not_authenticated");
    const fresh = await ensureSession();
    const retry = await fetch(path, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${fresh.token}`,
        ...(opts.headers || {}),
      },
    });
    if (!retry.ok) throw await apiError(retry);
    return retry.json();
  }
  if (!res.ok) throw await apiError(res);
  return res.json();
}

// Build an Error from a non-2xx response. The thrown error.message prefers
// the backend's `message` field (validation errors), falls back to `error`,
// and tags the HTTP status onto error.status so callers can branch on it.
async function apiError(res) {
  const body = await safeJson(res);
  const msg = body?.message || body?.error || `api_${res.status}`;
  const err = new Error(msg);
  err.status = res.status;
  err.body = body;
  return err;
}

// Open an authenticated binary endpoint in the user's system browser.
// We pass the bearer as a `?token=` query param so the backend can auth
// the request — the Telegram desktop client hands blob: URLs off to the
// OS, which has no app for them, so a real https URL via openLink is the
// only path that works inside Telegram. Used to preview pilot documents
// from the admin/superadmin portals.
export async function openAuthedFile(path) {
  const s = await ensureSession();
  const sep = path.includes("?") ? "&" : "?";
  const url = `${path}${sep}token=${encodeURIComponent(s.token)}`;
  const absolute = new URL(url, window.location.origin).toString();
  openLink(absolute);
}

async function safeJson(res) {
  try { return await res.json(); } catch { return null; }
}
